import { useEffect, useMemo, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	IconButton,
	InputAdornment,
	Paper,
	Stack,
	Step,
	StepLabel,
	Stepper,
	Switch,
	Tab,
	Tabs,
	TextField,
	Tooltip,
	Typography,
	createFilterOptions,
	styled,
} from '@mui/material';

import { sortByOrder, useApiClient, useApiEnvironments, useIsMobile, usePersistedSetting, usePersistedSize, useToastService } from '@@common';
import { DialogHeaderActions } from '@@components';

import {
	CreateServiceConnectionCommand,
	ParameterMetadata,
	ParameterValueOption,
	ParameterVisibilityCondition,
	ParameterVisibilityOperator,
	ServiceConnectionClient,
	ServiceConnectionModel,
	UpdateServiceConnectionRequest,
	WizardStepMetadata,
} from '../../api/api';
import StyledMarkdown from '../../common/components/StyledMarkdown';
import IconComponent from '../../common/components/IconComponent';
import CopyToClipboardButton from '../../components/Clipboard/CopyToClipboardButton';
import { PresenceIndicator } from '../../components/Presence/PresenceIndicator';
import { usePresence } from '../../components/Presence/usePresence';
import { ServiceConnectionRefModel } from '../FlowDesigner/Models/ServiceConnectionModel';
import useServiceConnectionMetadata from './Hooks/useServiceConnectionMetadata';
import ServiceConnectionObsoleteIndicator from './ServiceConnectionObsoleteIndicator';

const emptyServiceConnection: CreateServiceConnectionCommand = {
	name: '',
	type: '',
	isPerEnvironment: false,
	parameters: {},
	environmentParameters: [],
	enabledEnvironments: [],
};

type ServiceConnectionDialogProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
	onSuccess?: (value: ServiceConnectionRefModel) => void;
	mode: 'create' | 'edit';
	serviceConnectionToEdit?: ServiceConnectionModel;
	serviceConnectionType?: string;
	serviceConnectionCategory?: string;
};

// Reusable TextField component for service connection parameters
type ParameterTextFieldProps = {
	param: ParameterMetadata;
	value: unknown;
	onChange: (value: unknown) => void;
	keyPrefix?: string;
	valueOptions?: ParameterValueOption[];
	isLoadingValueOptions?: boolean;
	hasValueOptionsLookup?: boolean;
	readOnly?: boolean;
};

type DisplayServiceConnectionProperty = {
	label: string;
	description?: string;
	value: string;
};

type ParameterValueOptionsState = {
	options: ParameterValueOption[];
	isLoading: boolean;
	hasLookup: boolean;
};

type ScopedParameterMetadata = ParameterMetadata & {
	scope?: 'Environment' | 'NonEnvironment' | 0 | 1;
	parameterScope?: 'Environment' | 'NonEnvironment' | 0 | 1;
};

const defaultEnvironmentKey = 'default';
const DIALOG_SIZE_KEY = 'serviceConnectionDialog.size';
const DIALOG_MAXIMIZED_KEY = 'serviceConnectionDialog.maximized';
const filterParameterValueOptions = createFilterOptions<ParameterValueOption>({
	stringify: (option) => [option.name, option.value, option.description].filter(Boolean).join(' '),
});

function getParameterValueOptionsKey(environmentId: string | undefined, parameterName: string) {
	return `${environmentId ?? defaultEnvironmentKey}:${parameterName}`;
}

function hasDynamicParameterValueOptionsMetadata(param: { valueOptions?: ParameterValueOption[] | null }) {
	return Array.isArray(param.valueOptions) && param.valueOptions.length === 0;
}

function canRequestParameterValueOptions(param: { isSecret?: boolean; isStringMultiLine?: boolean; valueOptions?: ParameterValueOption[] | null }) {
	if (param.isSecret || param.isStringMultiLine) {
		return false;
	}

	return hasDynamicParameterValueOptionsMetadata(param);
}

function parameterValueToString(value: unknown): string {
	if (value === undefined || value === null) {
		return '';
	}

	if (typeof value === 'string') {
		return value;
	}

	if (typeof value === 'object') {
		return JSON.stringify(value);
	}

	return String(value);
}

function getOptionValue(option: ParameterValueOption): string {
	return parameterValueToString(option.value);
}

function getOptionLabel(option: ParameterValueOption): string {
	return option.name || getOptionValue(option);
}

function getCollectionParameterValues(value: unknown): string[] {
	if (Array.isArray(value)) {
		return value.map(parameterValueToString).filter(Boolean);
	}

	const stringValue = parameterValueToString(value);
	return stringValue ? [stringValue] : [];
}

function isBooleanParameter(param: ParameterMetadata) {
	return param.expectedType?.type === 'Boolean';
}

function getBooleanParameterValue(value: unknown): boolean {
	if (typeof value === 'boolean') {
		return value;
	}

	if (typeof value === 'string') {
		return value.toLowerCase() === 'true';
	}

	return Boolean(value);
}

function mergeValueOptions(...optionSets: (ParameterValueOption[] | null | undefined)[]) {
	const merged = new Map<string, ParameterValueOption>();

	optionSets
		.flatMap((options) => options ?? [])
		.forEach((option) => {
			const value = getOptionValue(option);
			if (!merged.has(value)) {
				merged.set(value, option);
			}
		});

	return Array.from(merged.values());
}

function isEmptyParameterValue(value: unknown): boolean {
	return value === undefined || value === null || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0);
}

function isParameterValueMissing(param: ParameterMetadata, value: unknown): boolean {
	if (param.isCollection && value === null) {
		return false;
	}

	return isEmptyParameterValue(value);
}

function areVisibilityValuesEqual(actualValue: unknown, expectedValue: unknown): boolean {
	if (typeof actualValue === 'boolean' || typeof expectedValue === 'boolean') {
		return getBooleanParameterValue(actualValue) === getBooleanParameterValue(expectedValue);
	}

	return parameterValueToString(actualValue) === parameterValueToString(expectedValue);
}

function isParameterVisibilityConditionMet(condition: ParameterVisibilityCondition, parameterValues: { [key: string]: any }): boolean {
	const actualValue = parameterValues[condition.nameRef];

	switch (condition.operator) {
		case ParameterVisibilityOperator.Equals:
			return areVisibilityValuesEqual(actualValue, condition.value);
		case ParameterVisibilityOperator.NotEquals:
			return !areVisibilityValuesEqual(actualValue, condition.value);
		case ParameterVisibilityOperator.HasValue:
			return !isEmptyParameterValue(actualValue);
		case ParameterVisibilityOperator.IsEmpty:
			return isEmptyParameterValue(actualValue);
		default:
			return true;
	}
}

function isParameterVisible(param: ParameterMetadata, parameterValues: { [key: string]: any }): boolean {
	return !param.visibleWhen?.length || param.visibleWhen.some((condition) => isParameterVisibilityConditionMet(condition, parameterValues));
}

function getOrderedWizardSteps(wizardSteps?: WizardStepMetadata[]): WizardStepMetadata[] {
	return [...(wizardSteps ?? [])].sort(sortByOrder);
}

function getWizardStepParameters(parameters: ParameterMetadata[], wizardSteps: WizardStepMetadata[], activeWizardStep: number): ParameterMetadata[] {
	if (wizardSteps.length === 0) {
		return parameters;
	}

	const currentStepKey = wizardSteps[activeWizardStep]?.key;
	const isLastStep = activeWizardStep === wizardSteps.length - 1;

	return parameters.filter((param) => (param.wizardStepKey ? param.wizardStepKey === currentStepKey : isLastStep));
}

function isNonEnvironmentParameter(param: ParameterMetadata): boolean {
	const scopedParam = param as ScopedParameterMetadata;
	const scope = scopedParam.scope ?? scopedParam.parameterScope;
	return scope === 'NonEnvironment' || scope === 1;
}

function getEnvironmentScopedParameters(parameters: ParameterMetadata[]): ParameterMetadata[] {
	return parameters.filter((param) => !isNonEnvironmentParameter(param));
}

function getNonEnvironmentScopedParameters(parameters: ParameterMetadata[]): ParameterMetadata[] {
	return parameters.filter(isNonEnvironmentParameter);
}

function pickParameterValues(parameterValues: { [key: string]: any }, parameters: ParameterMetadata[]): { [key: string]: any } {
	const names = new Set(parameters.map((param) => param.name));
	return Object.fromEntries(Object.entries(parameterValues).filter(([name]) => names.has(name)));
}

function getDraftValueOptionsDependencyKey(parameters: { [key: string]: any }, lookupParameters: ParameterMetadata[]): string {
	const lookupParameterNames = new Set(lookupParameters.map((param) => param.name));
	const dependencyEntries = Object.entries(parameters)
		.filter(([name]) => !lookupParameterNames.has(name))
		.sort(([left], [right]) => left.localeCompare(right));

	return JSON.stringify(dependencyEntries);
}

function areParameterValuesValid(parameters: ParameterMetadata[], parameterValues: { [key: string]: any }): boolean {
	return parameters.every((param) =>
		!isParameterVisible(param, parameterValues) ? true : param.isRequired ? !isParameterValueMissing(param, parameterValues[param.name]) : true,
	);
}

function getDisplayPropertyLabel(name: string): string {
	return name.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
}

function getDisplayPropertyValue(value: unknown, environmentId?: string): string | undefined {
	if (value === undefined || value === null) {
		return undefined;
	}

	if (typeof value === 'string') {
		return value;
	}

	if (typeof value === 'number' || typeof value === 'boolean') {
		return String(value);
	}

	if (Array.isArray(value)) {
		const environmentValue = value.find((item) => {
			return typeof item === 'object' && item !== null && 'environmentId' in item && item.environmentId === environmentId;
		});
		const selectedValue = environmentValue ?? value[0];

		return selectedValue ? getDisplayPropertyValue(selectedValue, environmentId) : undefined;
	}

	if (typeof value === 'object') {
		if ('url' in value && typeof value.url === 'string') {
			return value.url;
		}

		if ('value' in value) {
			return getDisplayPropertyValue(value.value, environmentId);
		}

		return JSON.stringify(value);
	}

	return String(value);
}

function getDisplayServiceConnectionProperties(
	properties: Awaited<ReturnType<ServiceConnectionClient['getProperties']>>,
	environmentId?: string,
): DisplayServiceConnectionProperty[] {
	return properties.flatMap((property) => {
		const value = getDisplayPropertyValue(property.value, environmentId);
		if (!value) {
			return [];
		}

		return [
			{
				label: getDisplayPropertyLabel(property.name),
				description: property.description,
				value,
			},
		];
	});
}

const ParameterGrid = styled(Box)({
	display: 'grid',
	gridTemplateColumns: 'minmax(0, 1fr)',
	gap: 20,
	alignItems: 'start',
	'@container connection-parameters (min-width: 720px)': {
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
	},
});

function ParameterTextField(props: ParameterTextFieldProps) {
	const { param } = props;
	return (
		<Box sx={{
			minWidth: 0,
			gridColumn: param.isStringMultiLine || param.isCollection || isBooleanParameter(param) ? '1 / -1' : undefined,
			'& .MuiFormControl-root, & .MuiAutocomplete-root': { width: '100%', minWidth: 0 },
			'& .MuiOutlinedInput-root': { bgcolor: 'background.default', borderRadius: 1.5 },
			'& .MuiFormHelperText-root': { mx: 0.5, mt: 0.75, lineHeight: 1.5, overflowWrap: 'anywhere' },
			'& .MuiFormControlLabel-root': { ml: 0, mr: 0, gap: 1 },
		}}>
			<ParameterInput {...props} />
		</Box>
	);
}

function ParameterInput({
	param,
	value,
	onChange,
	keyPrefix = '',
	valueOptions,
	isLoadingValueOptions = false,
	hasValueOptionsLookup = false,
	readOnly = false,
}: ParameterTextFieldProps) {
	const isSecretMultiline = param.isSecret && param.isStringMultiLine;
	const options = mergeValueOptions(param.valueOptions, valueOptions);
	const stringValue = parameterValueToString(value);
	const canUseValueOptions = !param.isSecret && !param.isStringMultiLine && (hasValueOptionsLookup || isLoadingValueOptions || options.length > 0);
	const allowCustomValue = hasValueOptionsLookup;
	const [showAllOptions, setShowAllOptions] = useState(false);
	const [collectionFilter, setCollectionFilter] = useState('');

	if (isBooleanParameter(param)) {
		const label = `${param.displayName ?? param.name}${param.isRequired ? ' *' : ''}`;

		return (
			<Box key={keyPrefix ? `${keyPrefix}-${param.name}` : param.name}>
				<FormControlLabel control={<Switch checked={getBooleanParameterValue(value)} disabled={readOnly} onChange={(e) => onChange(e.target.checked)} />} label={label} />
				{param.description && (
					<Typography variant="caption" color="textSecondary" display="block" sx={{ mt: -0.5 }}>
						{param.description}
					</Typography>
				)}
			</Box>
		);
	}

	if (param.isCollection && !canUseValueOptions) {
		return (
			<Autocomplete<string, true, false, true>
				key={keyPrefix ? `${keyPrefix}-${param.name}` : param.name}
				multiple
				freeSolo
				options={[]}
				value={getCollectionParameterValues(value)}
				readOnly={readOnly}
				onChange={(_, newValue) => onChange(newValue.map(parameterValueToString).filter(Boolean))}
				renderInput={(params) => (
					<TextField
						{...params}
						size="small"
						label={param.displayName ?? param.name}
						required={param.isRequired ?? false}
						autoComplete="off"
						fullWidth
						variant="outlined"
						helperText={`${param.description ?? ''}${param.description ? ' ' : ''}Press Enter after each value.`}
					/>
				)}
			/>
		);
	}

	if (canUseValueOptions) {
		if (param.isCollection) {
			const selectedValues = getCollectionParameterValues(value);
			const selectedValueSet = new Set(selectedValues);
			const isAllSelected = value === null;
			const normalizedFilter = collectionFilter.trim().toLowerCase();
			const filteredOptions = normalizedFilter
				? options.filter((option) => [getOptionLabel(option), getOptionValue(option), option.description].some((part) => part?.toLowerCase().includes(normalizedFilter)))
				: options;
			const toggleValue = (optionValue: string, checked: boolean) => {
				const nextValues = new Set(selectedValues);
				if (checked) {
					nextValues.add(optionValue);
				} else {
					nextValues.delete(optionValue);
				}

				onChange(Array.from(nextValues));
			};

			return (
				<Box key={keyPrefix ? `${keyPrefix}-${param.name}` : param.name}>
					<Typography variant="subtitle2" gutterBottom>
						{param.displayName ?? param.name}
						{param.isRequired ? ' *' : ''}
					</Typography>
					<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
						<TextField
							label="Search"
							size="small"
							value={collectionFilter}
							onChange={(event) => setCollectionFilter(event.target.value)}
							fullWidth
							autoComplete="off"
							slotProps={{
								input: {
									endAdornment: isLoadingValueOptions ? <CircularProgress color="inherit" size={18} /> : undefined,
								},
							}}
						/>
						<FormControlLabel
							sx={{ minWidth: { sm: 112 } }}
							control={
								<Checkbox
									checked={isAllSelected}
									disabled={readOnly}
									onChange={(event) => {
										onChange(event.target.checked ? null : []);
									}}
								/>
							}
							label="All"
						/>
					</Stack>
					{param.description && (
						<Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.75 }}>
							{param.description}
						</Typography>
					)}
					<Box sx={{ mt: 1, border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 360, overflowY: 'auto' }}>
						{filteredOptions.length === 0 && (
							<Typography variant="body2" color="textSecondary" sx={{ p: 1.5 }}>
								{isLoadingValueOptions ? 'Loading options...' : 'No options found'}
							</Typography>
						)}
						{filteredOptions.map((option) => {
							const optionValue = getOptionValue(option);
							const checked = isAllSelected || selectedValueSet.has(optionValue);
							return (
								<Box
									key={optionValue}
									component="label"
									sx={{
										display: 'flex',
										gap: 1,
										alignItems: 'flex-start',
										p: 1.25,
										cursor: isAllSelected ? 'default' : 'pointer',
										borderTop: 1,
										borderColor: 'divider',
										'&:first-of-type': { borderTop: 0 },
									}}
								>
									<Checkbox
										checked={checked}
										disabled={readOnly || isAllSelected}
										onChange={(event) => toggleValue(optionValue, event.target.checked)}
										sx={{ mt: -0.75 }}
									/>
									<Box minWidth={0}>
										<Typography variant="body2">{getOptionLabel(option)}</Typography>
										{option.description && (
											<Typography variant="caption" color="textSecondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
												{option.description}
											</Typography>
										)}
									</Box>
								</Box>
							);
						})}
					</Box>
				</Box>
			);
		}

		const selectedOption = options.find((option) => getOptionValue(option) === stringValue) ?? null;
		const autocompleteValue = allowCustomValue ? selectedOption ?? stringValue : selectedOption;
		const autocompleteInputValue = allowCustomValue || selectedOption ? stringValue : '';

		return (
			<Autocomplete<ParameterValueOption, false, false, boolean>
				key={keyPrefix ? `${keyPrefix}-${param.name}` : param.name}
				freeSolo={allowCustomValue}
				options={options}
				value={autocompleteValue}
				inputValue={autocompleteInputValue}
				loading={isLoadingValueOptions}
				readOnly={readOnly}
				forcePopupIcon
				noOptionsText={isLoadingValueOptions ? 'Loading options...' : 'No options found'}
				filterOptions={(availableOptions, state) => (showAllOptions ? availableOptions : filterParameterValueOptions(availableOptions, state))}
				getOptionLabel={(option) => (typeof option === 'string' ? option : getOptionLabel(option))}
				isOptionEqualToValue={(option, selected) => typeof selected !== 'string' && getOptionValue(option) === getOptionValue(selected)}
				onOpen={() => setShowAllOptions(true)}
				onClose={() => setShowAllOptions(false)}
				onChange={(_, newValue) => {
					if (typeof newValue === 'string') {
						onChange(newValue);
						return;
					}

					onChange(newValue ? getOptionValue(newValue) : '');
				}}
				onInputChange={(_, newInputValue, reason) => {
					if (allowCustomValue && reason !== 'reset') {
						setShowAllOptions(false);
						onChange(newInputValue);
					}
				}}
				renderOption={({ key, ...props }, option) => (
					<Box key={key} component="li" {...props}>
						<Box>
							<Typography variant="body2">{getOptionLabel(option)}</Typography>
							{option.description && (
								<Typography variant="caption" color="textSecondary">
									{option.description}
								</Typography>
							)}
						</Box>
					</Box>
				)}
				renderInput={(params) => (
					<TextField
						{...params}
						size="small"
						label={param.displayName ?? param.name}
						required={param.isRequired ?? false}
						autoComplete="off"
						fullWidth
						variant="outlined"
						helperText={param.description}
						slotProps={{
							input: {
								...params.InputProps,
								endAdornment: (
									<>
										{isLoadingValueOptions ? <CircularProgress color="inherit" size={18} /> : null}
										{params.InputProps.endAdornment}
										<InputAdornment position="end">
											<CopyToClipboardButton value={stringValue} />
										</InputAdornment>
									</>
								),
							},
							htmlInput: params.inputProps,
						}}
					/>
				)}
			/>
		);
	}

	return (
		<TextField
			size="small"
			key={keyPrefix ? `${keyPrefix}-${param.name}` : param.name}
			label={param.displayName ?? param.name}
			required={param.isRequired ?? false}
			type={param.isSecret && !param.isStringMultiLine ? 'password' : 'text'}
			autoComplete={param.isSecret ? 'new-password' : 'off'}
			value={stringValue}
			disabled={readOnly}
			onChange={(e) => onChange(e.target.value)}
			fullWidth
			variant="outlined"
			multiline={param.isStringMultiLine}
			minRows={param.isStringMultiLine ? 3 : undefined}
			helperText={param.description}
			slotProps={{
				input: {
					endAdornment: (
						<InputAdornment position="end">
							<CopyToClipboardButton value={stringValue} />
						</InputAdornment>
					),
					sx: isSecretMultiline
						? {
								'& textarea': {
									WebkitTextSecurity: 'disc',
									fontFamily: 'monospace',
								},
							}
						: undefined,
				},
			}}
		/>
	);
}

function ServiceConnectionProperty({ label, description, value }: DisplayServiceConnectionProperty) {
	return (
		<Box>
			<Typography variant="subtitle2" color="textPrimary" fontWeight={600}>
				{label}
			</Typography>
			<Box sx={{ pl: 2, mt: 0.75 }}>
				<Box display="flex" alignItems="center" gap={1}>
					<Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
						{value}
					</Typography>
					<CopyToClipboardButton value={value} title={`Copy ${label} to clipboard`} edge={false} />
				</Box>
				{description && (
					<Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
						{description}
					</Typography>
				)}
			</Box>
		</Box>
	);
}

function PropertiesContent({ isLoading, properties }: { isLoading: boolean; properties?: DisplayServiceConnectionProperty[] }) {
	if (!isLoading && (!properties || properties.length === 0)) {
		return null;
	}

	return (
		<Stack spacing={2}>
			{isLoading ? (
				<Box display="flex" alignItems="center" gap={2}>
					<CircularProgress size={20} />
					<Typography variant="body2" color="textSecondary">
						Loading properties...
					</Typography>
				</Box>
			) : (
				properties?.map((property) => <ServiceConnectionProperty key={property.label} {...property} />)
			)}
		</Stack>
	);
}

export default function ServiceConnectionDialog({ open, setOpen, onSuccess, mode, serviceConnectionToEdit, serviceConnectionType, serviceConnectionCategory }: ServiceConnectionDialogProps) {
	const viewers = usePresence('service-connection', open && mode === 'edit' ? serviceConnectionToEdit?.id : undefined, 'definition', null);
	const isMobile = useIsMobile();
	const { serviceConnectionsMetadata, serviceConnectionByName, isLoading: isLoadingServiceConnectionMetadata } = useServiceConnectionMetadata();
	const [serviceConnection, setServiceConnection] = useState<CreateServiceConnectionCommand>(emptyServiceConnection);
	const [activeTab, setActiveTab] = useState(0);
	const [propertiesTab, setPropertiesTab] = useState(0);
	const [dialogTab, setDialogTab] = useState(0);
	const [activeWizardStep, setActiveWizardStep] = useState(0);
	const [isMaximized, setIsMaximized] = usePersistedSetting(DIALOG_MAXIMIZED_KEY, false);
	const { ref: paperRef, size: storedSize } = usePersistedSize(DIALOG_SIZE_KEY, open);

	const toastService = useToastService();
	const client = useApiClient(ServiceConnectionClient);
	const [saving, setSaving] = useState(false);
	const [serviceConnectionProperties, setServiceConnectionProperties] = useState<Record<string, DisplayServiceConnectionProperty[]>>({});
	const [isLoadingServiceConnectionProperties, setIsLoadingServiceConnectionProperties] = useState(false);
	const [parameterValueOptions, setParameterValueOptions] = useState<Record<string, ParameterValueOptionsState>>({});
	const [serviceConnectionManual, setServiceConnectionManual] = useState<string>();
	const [isLoadingServiceConnectionManual, setIsLoadingServiceConnectionManual] = useState(false);
	const [serviceConnectionTypeSearch, setServiceConnectionTypeSearch] = useState('');
	const [serviceConnectionTypeCategory, setServiceConnectionTypeCategory] = useState<string | null>(null);
	const isReadOnly = mode === 'edit' && serviceConnectionToEdit?.isReadOnly === true;

	// Fetch environments for per-environment parameter collection
	const { data: environments, isLoading: isLoadingEnvironments } = useApiEnvironments();

	const serviceConnectionMetadata = useMemo(
		() => (serviceConnection?.type ? serviceConnectionByName.get(serviceConnection.type) : undefined),
		[serviceConnectionByName, serviceConnection.type],
	);

	const wizardSteps = useMemo(() => getOrderedWizardSteps(serviceConnectionMetadata?.wizardSteps), [serviceConnectionMetadata?.wizardSteps]);
	const useWizard = mode === 'create' && wizardSteps.length > 0;
	const isLastWizardStep = !useWizard || activeWizardStep === wizardSteps.length - 1;
	const activeWizardStepMetadata = useWizard ? wizardSteps[activeWizardStep] : undefined;
	const sortedParameters = useMemo(() => [...(serviceConnectionMetadata?.parameters ?? [])].sort(sortByOrder), [serviceConnectionMetadata?.parameters]);
	const activeParameters = useMemo(
		() => getWizardStepParameters(sortedParameters, useWizard ? wizardSteps : [], activeWizardStep),
		[activeWizardStep, sortedParameters, useWizard, wizardSteps],
	);
	const activeNonEnvironmentParameters = useMemo(
		() => getNonEnvironmentScopedParameters(activeParameters),
		[activeParameters],
	);
	const activeEnvironmentParameters = useMemo(
		() => getEnvironmentScopedParameters(activeParameters),
		[activeParameters],
	);
	const activeDraftValueOptionsCandidateParameters = useMemo(
		() => activeParameters.filter(canRequestParameterValueOptions),
		[activeParameters],
	);
	const draftValueOptionsDependencyKey = useMemo(
		() => getDraftValueOptionsDependencyKey(serviceConnection.parameters, activeDraftValueOptionsCandidateParameters),
		[activeDraftValueOptionsCandidateParameters, serviceConnection.parameters],
	);
	const activeDraftValueOptionsParameters = useMemo(
		() => {
			const dependencyParameters = Object.fromEntries(JSON.parse(draftValueOptionsDependencyKey) as [string, any][]);
			return activeDraftValueOptionsCandidateParameters.filter((param) => isParameterVisible(param, dependencyParameters));
		},
		[activeDraftValueOptionsCandidateParameters, draftValueOptionsDependencyKey],
	);

	// Group service connections by category for type selection
	const groupedServiceConnections = useMemo(() => {
		if (!serviceConnectionsMetadata) return [];

		const availableMetadata = mode === 'create'
			? serviceConnectionsMetadata.filter((sc) => serviceConnectionType
				? sc.name === serviceConnectionType
				: !serviceConnectionCategory || sc.categories?.includes(serviceConnectionCategory))
			: serviceConnectionsMetadata;
		const grouped = availableMetadata.reduce(
			(acc, sc) => {
				const category = sc.category || 'Other';
				if (!acc[category]) {
					acc[category] = [];
				}
				acc[category].push(sc);
				return acc;
			},
			{} as Record<string, typeof serviceConnectionsMetadata>,
		);

		return Object.entries(grouped)
			.sort(([a], [b]) => a.localeCompare(b))
			.flatMap(([category, items]) => items.map((item) => ({ ...item, category })));
	}, [serviceConnectionsMetadata, mode, serviceConnectionType, serviceConnectionCategory]);
	const selectedServiceConnectionType = useMemo(
		() => groupedServiceConnections.find((sc) => sc.name === serviceConnection?.type) || null,
		[groupedServiceConnections, serviceConnection?.type],
	);
	const searchedServiceConnectionTypes = useMemo(() => {
		const search = serviceConnectionTypeSearch.trim().toLowerCase();
		if (!search) {
			return groupedServiceConnections;
		}

		return groupedServiceConnections.filter((option) => {
			const name = (option.displayName ?? option.name).toLowerCase();
			const category = option.category?.toLowerCase() ?? '';
			return name.includes(search) || category.includes(search);
		});
	}, [groupedServiceConnections, serviceConnectionTypeSearch]);
	const serviceConnectionTypeCategories = useMemo(
		() => Array.from(new Set(groupedServiceConnections.map((option) => option.category || 'Other'))).sort((left, right) => left.localeCompare(right)),
		[groupedServiceConnections],
	);
	const matchingServiceConnectionTypeCategories = useMemo(
		() => new Set(searchedServiceConnectionTypes.map((option) => option.category || 'Other')),
		[searchedServiceConnectionTypes],
	);
	const filteredServiceConnectionTypes = useMemo(
		() => searchedServiceConnectionTypes.filter((option) => !serviceConnectionTypeCategory || (option.category || 'Other') === serviceConnectionTypeCategory),
		[searchedServiceConnectionTypes, serviceConnectionTypeCategory],
	);
	const filteredServiceConnectionTypeGroups = useMemo(() => {
		return Object.entries(
			filteredServiceConnectionTypes.reduce(
				(acc, serviceConnectionType) => {
					const category = serviceConnectionType.category || 'Other';
					acc[category] = [...(acc[category] ?? []), serviceConnectionType];
					return acc;
				},
				{} as Record<string, typeof filteredServiceConnectionTypes>,
			),
		).sort(([left], [right]) => left.localeCompare(right));
	}, [filteredServiceConnectionTypes]);

	// Initialize form with data when editing
	useEffect(() => {
		if (mode === 'edit' && serviceConnectionToEdit && open) {
			// Reset tab to first tab when opening
			setDialogTab(0);
			setActiveTab(0);
			setPropertiesTab(0);
			setActiveWizardStep(0);
			// For edit mode, we'll load the full service connection details including parameters
			const loadServiceConnectionDetails = async () => {
				try {
					const fullServiceConnection = await client.getById(serviceConnectionToEdit.id, true);
					setServiceConnection({
						name: fullServiceConnection.name,
						type: fullServiceConnection.type,
						isPerEnvironment: fullServiceConnection.isPerEnvironment,
						parameters: fullServiceConnection.parameters || {},
						environmentParameters: (fullServiceConnection.environmentParameters || []).map((environmentParameters) => ({
							...environmentParameters,
							parameters: environmentParameters.parameters ?? {},
						})),
						enabledEnvironments: fullServiceConnection.enabledEnvironments || [],
					});
				} catch (error) {
					console.error('Error loading service connection details:', error);
					toastService.error('Failed to load service connection details');
				}
			};

			loadServiceConnectionDetails();
		} else if (mode === 'create') {
			setServiceConnection({ ...emptyServiceConnection, type: serviceConnectionType ?? '' });
			setDialogTab(0);
			setActiveTab(0); // Reset tab for create mode too
			setPropertiesTab(0);
			setActiveWizardStep(0);
		}
	}, [mode, serviceConnectionToEdit, open, client, toastService, serviceConnectionType]);

	useEffect(() => {
		setActiveWizardStep(0);
		setParameterValueOptions({});
	}, [serviceConnection.type]);

	useEffect(() => {
		if (serviceConnectionTypeCategory && !serviceConnectionTypeCategories.includes(serviceConnectionTypeCategory)) {
			setServiceConnectionTypeCategory(null);
		}
	}, [serviceConnectionTypeCategories, serviceConnectionTypeCategory]);

	useEffect(() => {
		if (activeWizardStep >= wizardSteps.length) {
			setActiveWizardStep(Math.max(wizardSteps.length - 1, 0));
		}
	}, [activeWizardStep, wizardSteps.length]);

	useEffect(() => {
		if (!open || !serviceConnection.type) {
			setServiceConnectionManual(undefined);
			setIsLoadingServiceConnectionManual(false);
			setDialogTab(0);
			return;
		}

		const abortController = new AbortController();

		const loadManual = async () => {
			setIsLoadingServiceConnectionManual(true);
			try {
				const manual = await client.getManual(serviceConnection.type, abortController.signal);
				if (!abortController.signal.aborted) {
					setServiceConnectionManual(manual?.trim() ? manual : undefined);
				}
			} catch {
				if (!abortController.signal.aborted) {
					setServiceConnectionManual(undefined);
					setDialogTab(0);
				}
			} finally {
				if (!abortController.signal.aborted) {
					setIsLoadingServiceConnectionManual(false);
				}
			}
		};

		loadManual();

		return () => {
			abortController.abort();
		};
	}, [open, serviceConnection.type, client]);

	useEffect(() => {
		if (mode !== 'edit' || !open || !serviceConnectionToEdit?.id || isLoadingEnvironments) {
			setServiceConnectionProperties({});
			setIsLoadingServiceConnectionProperties(false);
			return;
		}

		const abortController = new AbortController();

		const loadProperties = async () => {
			setIsLoadingServiceConnectionProperties(true);
			const loadedProperties: Record<string, DisplayServiceConnectionProperty[]> = {};
			const environmentsToQuery = environments?.length ? environments : [undefined];

			await Promise.all(
				environmentsToQuery.map(async (env) => {
					try {
						const properties = await client.getProperties(serviceConnectionToEdit.id, env?.id, abortController.signal);
						loadedProperties[env?.id ?? defaultEnvironmentKey] = getDisplayServiceConnectionProperties(properties, env?.id);
					} catch {
						// Connectors without exposed properties simply do not render this section.
					}
				}),
			);

			if (!abortController.signal.aborted) {
				setServiceConnectionProperties(loadedProperties);
				setIsLoadingServiceConnectionProperties(false);
			}
		};

		loadProperties();

		return () => {
			abortController.abort();
		};
	}, [mode, open, serviceConnectionToEdit?.id, client, environments, isLoadingEnvironments]);

	useEffect(() => {
		const parameters = serviceConnectionMetadata?.parameters ?? [];
		const lookupParameters = parameters.filter(canRequestParameterValueOptions);
		const nonEnvironmentLookupParameters = getNonEnvironmentScopedParameters(lookupParameters);
		const environmentLookupParameters = getEnvironmentScopedParameters(lookupParameters);

		if (mode !== 'edit' || !open || !serviceConnectionToEdit?.id || lookupParameters.length === 0 || isLoadingEnvironments) {
			setParameterValueOptions({});
			return;
		}

		const abortController = new AbortController();
		const valueOptionRequests = serviceConnection.isPerEnvironment
			? [
					...nonEnvironmentLookupParameters.map((param) => ({ env: undefined, param })),
					...(environments ?? []).flatMap((env) => environmentLookupParameters.map((param) => ({ env, param }))),
				]
			: lookupParameters.map((param) => ({ env: undefined, param }));

		if (serviceConnection.isPerEnvironment && environmentLookupParameters.length > 0 && (!environments || environments.length === 0)) {
			setParameterValueOptions({});
			return;
		}

		const loadingValueOptions = lookupParameters.reduce(
			(acc, param) => {
				if (hasDynamicParameterValueOptionsMetadata(param)) {
					valueOptionRequests
						.filter((request) => request.param.name === param.name)
						.forEach(({ env }) => {
							acc[getParameterValueOptionsKey(env?.id, param.name)] = {
								options: param.valueOptions ?? [],
								isLoading: true,
								hasLookup: true,
							};
						});
				}

				return acc;
			},
			{} as Record<string, ParameterValueOptionsState>,
		);

		setParameterValueOptions(loadingValueOptions);

		const loadParameterValueOptions = async () => {
			const loadedValueOptions: Record<string, ParameterValueOptionsState> = {};

			await Promise.all(
				valueOptionRequests.map(
					async ({ env, param }) => {
						const key = getParameterValueOptionsKey(env?.id, param.name);
						try {
							const options = await client.getParameterValueOptions(serviceConnectionToEdit.id, param.name, env?.id, abortController.signal);
							const mergedOptions = mergeValueOptions(param.valueOptions ?? undefined, options);
							loadedValueOptions[key] = {
								options: mergedOptions,
								isLoading: false,
								hasLookup: hasDynamicParameterValueOptionsMetadata(param) || mergedOptions.length > 0,
							};
						} catch {
							loadedValueOptions[key] = {
								options: param.valueOptions ?? [],
								isLoading: false,
								hasLookup: false,
							};
						}
					},
				),
			);

			if (!abortController.signal.aborted) {
				setParameterValueOptions(loadedValueOptions);
			}
		};

		loadParameterValueOptions();

		return () => {
			abortController.abort();
		};
	}, [
		mode,
		open,
		serviceConnectionToEdit?.id,
		serviceConnectionMetadata?.parameters,
		serviceConnection.isPerEnvironment,
		client,
		environments,
		isLoadingEnvironments,
	]);

	useEffect(() => {
		const lookupParameters = activeDraftValueOptionsParameters;

		if (mode !== 'create' || !open || !serviceConnection.type || lookupParameters.length === 0) {
			return;
		}

		const abortController = new AbortController();
		const loadingValueOptions = lookupParameters.reduce(
			(acc, param) => {
				acc[getParameterValueOptionsKey(undefined, param.name)] = {
					options: param.valueOptions ?? [],
					isLoading: true,
					hasLookup: true,
				};

				return acc;
			},
			{} as Record<string, ParameterValueOptionsState>,
		);

		setParameterValueOptions((prev) => ({ ...prev, ...loadingValueOptions }));

		const loadDraftParameterValueOptions = async () => {
			const loadedValueOptions: Record<string, ParameterValueOptionsState> = {};

			await Promise.all(
				lookupParameters.map(async (param) => {
					const key = getParameterValueOptionsKey(undefined, param.name);
					try {
						const options = await client.getDraftParameterValueOptions(
							serviceConnection.type,
							param.name,
							{ parameters: Object.fromEntries(JSON.parse(draftValueOptionsDependencyKey) as [string, any][]) },
							abortController.signal,
						);
						const mergedOptions = mergeValueOptions(param.valueOptions ?? undefined, options);
						loadedValueOptions[key] = {
							options: mergedOptions,
							isLoading: false,
							hasLookup: hasDynamicParameterValueOptionsMetadata(param) || mergedOptions.length > 0,
						};
					} catch {
						loadedValueOptions[key] = {
							options: param.valueOptions ?? [],
							isLoading: false,
							hasLookup: false,
						};
					}
				}),
			);

			if (!abortController.signal.aborted) {
				setParameterValueOptions((prev) => ({ ...prev, ...loadedValueOptions }));
			}
		};

		loadDraftParameterValueOptions();

		return () => {
			abortController.abort();
		};
	}, [activeDraftValueOptionsParameters, client, draftValueOptionsDependencyKey, mode, open, serviceConnection.type]);

	// Initialize parameters with default values when the connection type changes
	useEffect(() => {
		if (serviceConnectionMetadata?.parameters?.length && serviceConnection.type) {
			const nonEnvironmentParameters = getNonEnvironmentScopedParameters(serviceConnectionMetadata.parameters);
			const environmentParameters = getEnvironmentScopedParameters(serviceConnectionMetadata.parameters);

			if (serviceConnection.isPerEnvironment && environments) {
				// For per-environment connections, ensure each environment has parameter entries
				const updatedEnvParams = [...serviceConnection.environmentParameters];
				const updatedParameters = { ...serviceConnection.parameters };
				let hasChanges = false;

				nonEnvironmentParameters.forEach((param) => {
					if (updatedParameters[param.name] === undefined) {
						updatedParameters[param.name] = param.defaultValue ?? '';
						hasChanges = true;
					}
				});

				environments.forEach((env) => {
					const existingEnvIndex = updatedEnvParams.findIndex((ep) => ep.environmentId === env.id);
					let envParams = existingEnvIndex >= 0 ? updatedEnvParams[existingEnvIndex] : undefined;
					if (!envParams) {
						envParams = { environmentId: env.id, parameters: {} };
						updatedEnvParams.push(envParams);
						hasChanges = true;
					} else if (!envParams.parameters) {
						envParams = { ...envParams, parameters: {} };
						updatedEnvParams[existingEnvIndex] = envParams;
						hasChanges = true;
					}

					// Add default values for missing parameters
					environmentParameters.forEach((param) => {
						if (envParams!.parameters[param.name] === undefined) {
							envParams!.parameters[param.name] = param.defaultValue ?? '';
							hasChanges = true;
						}
					});
				});

				if (hasChanges) {
					setServiceConnection((prev) => ({
						...prev,
						parameters: updatedParameters,
						environmentParameters: updatedEnvParams,
					}));
				}
			} else {
				// For regular connections, create default parameters for any missing parameters
				const currentParams = { ...serviceConnection.parameters };
				let hasChanges = false;

				serviceConnectionMetadata.parameters.forEach((param) => {
					if (currentParams[param.name] === undefined) {
						currentParams[param.name] = param.defaultValue ?? '';
						hasChanges = true;
					}
				});

				// Only update if we added new default values
				if (hasChanges) {
					setServiceConnection((prev) => ({
						...prev,
						parameters: currentParams,
					}));
				}
			}
		}
	}, [
		serviceConnectionMetadata,
		serviceConnection.type,
		serviceConnection.isPerEnvironment,
		environments,
		serviceConnection.parameters,
		serviceConnection.environmentParameters,
	]);

	const handleClose = () => {
		setOpen(false);
		setDialogTab(0);
		setActiveTab(0); // Reset to first tab
		setPropertiesTab(0);
		setActiveWizardStep(0);
		setServiceConnectionTypeSearch('');
		setServiceConnectionTypeCategory(null);
		if (mode === 'create') {
			setServiceConnection(emptyServiceConnection);
		}
	};

	const handlePreviousWizardStep = () => {
		setActiveWizardStep((step) => Math.max(step - 1, 0));
	};

	const handleNextWizardStep = () => {
		setActiveWizardStep((step) => Math.min(step + 1, wizardSteps.length - 1));
	};

	const handleSave = async () => {
		if (isReadOnly) {
			return;
		}

		setSaving(true);
		try {
			const metadataParameters = serviceConnectionMetadata?.parameters ?? [];
			const nonEnvironmentParameters = getNonEnvironmentScopedParameters(metadataParameters);
			const environmentParameters = getEnvironmentScopedParameters(metadataParameters);
			const rootParameters = serviceConnection.isPerEnvironment ? nonEnvironmentParameters : metadataParameters;
			if (mode === 'create') {
				// Clean the service connection parameters before creating
				const cleanedServiceConnection: CreateServiceConnectionCommand = {
					...serviceConnection,
					parameters: cleanParameters(serviceConnection.parameters, rootParameters),
					environmentParameters: serviceConnection.environmentParameters.map((ep) => ({
						...ep,
						parameters: cleanParameters(ep.parameters, environmentParameters, { ...serviceConnection.parameters, ...ep.parameters }),
					})),
				};
				const serviceConnectionId = await client.create(cleanedServiceConnection);
				toastService.success(`Service Connection '${serviceConnection.name}' created successfully.`);
				onSuccess?.({ Id: serviceConnectionId, Name: serviceConnection.name });
			} else {
				// Edit mode - update the service connection
				const updateRequest: UpdateServiceConnectionRequest = {
					name: serviceConnection.name,
					isPerEnvironment: serviceConnection.isPerEnvironment,
					parameters: cleanParameters(serviceConnection.parameters, rootParameters),
					environmentParameters: serviceConnection.isPerEnvironment
						? serviceConnection.environmentParameters.map((ep) => ({
								...ep,
								parameters: cleanParameters(ep.parameters, environmentParameters, { ...serviceConnection.parameters, ...ep.parameters }),
							}))
						: undefined,
					enabledEnvironments: serviceConnection.enabledEnvironments,
				};
				await client.update(serviceConnectionToEdit!.id, updateRequest);

				toastService.success(`Service Connection '${serviceConnection.name}' updated successfully.`);
				onSuccess?.({ Id: serviceConnectionToEdit!.id, Name: serviceConnection.name });
			}

			if (mode === 'create') {
				setServiceConnection(emptyServiceConnection);
			}
			setOpen(false);
		} catch (error) {
			const action = mode === 'create' ? 'create' : 'update';
			toastService.error(`Failed to ${action} Service Connection: ${error instanceof Error ? error.message : 'Unknown error'}`);
		} finally {
			setSaving(false);
		}
	};

	// Validation: all required fields must be set
	const isFormValid = useMemo(() => {
		if (!serviceConnectionMetadata) {
			return mode === 'edit' ? !!serviceConnection.name.trim() : false;
		}

		const metadataParameters = serviceConnectionMetadata.parameters ?? [];
		const nonEnvironmentParameters = getNonEnvironmentScopedParameters(metadataParameters);
		const environmentParameters = getEnvironmentScopedParameters(metadataParameters);
		const allParamsFilled = serviceConnection.isPerEnvironment
			? areParameterValuesValid(nonEnvironmentParameters, serviceConnection.parameters)
			: areParameterValuesValid(metadataParameters, serviceConnection.parameters);

		// For per-environment connections, validate that all environments have all required parameters
		let allEnvironmentParamsFilled = true;
		if (serviceConnection.isPerEnvironment && environmentParameters.length > 0) {
			if (!environments) {
				allEnvironmentParamsFilled = false;
			} else {
				allEnvironmentParamsFilled = environments.every((env) => {
					const envParams = serviceConnection.environmentParameters.find((ep) => ep.environmentId === env.id);
					if (!envParams) return false;

					return areParameterValuesValid(environmentParameters, { ...serviceConnection.parameters, ...envParams.parameters });
				});
			}
		}

		if (mode === 'edit') {
			// In edit mode, validate the name field and parameters
			return !!serviceConnection.name.trim() && allParamsFilled && allEnvironmentParamsFilled;
		}

		// In create mode, validate all fields
		return !!serviceConnection.type && !!serviceConnection.name.trim() && allParamsFilled && allEnvironmentParamsFilled;
	}, [mode, serviceConnectionMetadata, serviceConnection, environments]);

	const isCurrentWizardStepValid = useMemo(() => {
		if (!useWizard) {
			return isFormValid;
		}

		if (activeWizardStep === 0 && (!serviceConnection.type || !serviceConnection.name.trim())) {
			return false;
		}

		if (serviceConnection.isPerEnvironment) {
			const nonEnvironmentParametersValid = areParameterValuesValid(activeNonEnvironmentParameters, serviceConnection.parameters);
			if (!nonEnvironmentParametersValid || activeEnvironmentParameters.length === 0) {
				return nonEnvironmentParametersValid;
			}

			if (!environments) {
				return false;
			}

			return environments.every((env) => {
				const envParams = serviceConnection.environmentParameters.find((ep) => ep.environmentId === env.id);
				return !!envParams && areParameterValuesValid(activeEnvironmentParameters, { ...serviceConnection.parameters, ...envParams.parameters });
			});
		}

		return areParameterValuesValid(activeParameters, serviceConnection.parameters);
	}, [activeEnvironmentParameters, activeNonEnvironmentParameters, activeParameters, activeWizardStep, environments, isFormValid, serviceConnection, useWizard]);

	const editDialogConnectionName = serviceConnection.name || serviceConnectionToEdit?.name;
	const dialogTitle = mode === 'create' ? 'Create Service Connection' : `${isReadOnly ? 'View' : 'Edit'} Service Connection${editDialogConnectionName ? ` - ${editDialogConnectionName}` : ''}`;
	const isEditingType = mode === 'edit';
	const hasGuideTab = isLoadingServiceConnectionManual || !!serviceConnectionManual;
	useEffect(() => {
		if (!hasGuideTab && dialogTab === 1) {
			setDialogTab(0);
		}
	}, [dialogTab, hasGuideTab]);

	// Helper function to handle environment parameter changes
	const handleEnvironmentParameterChange = (environmentId: string, paramName: string, value: unknown) => {
		setServiceConnection((prev) => {
			const envParams = [...prev.environmentParameters];
			const existingEnvIndex = envParams.findIndex((ep) => ep.environmentId === environmentId);

			if (existingEnvIndex >= 0) {
				// Update existing environment parameters
				envParams[existingEnvIndex] = {
					...envParams[existingEnvIndex],
					parameters: {
						...envParams[existingEnvIndex].parameters,
						[paramName]: value,
					},
				};
			} else {
				// Create new environment parameters entry
				envParams.push({
					environmentId,
					parameters: { [paramName]: value },
				});
			}

			return { ...prev, environmentParameters: envParams };
		});
	};

	// Helper function to get environment parameter value
	const getEnvironmentParameterValue = (environmentId: string, paramName: string): unknown => {
		const envParams = serviceConnection.environmentParameters.find((ep) => ep.environmentId === environmentId);
		const parameters = envParams?.parameters;
		return parameters && Object.prototype.hasOwnProperty.call(parameters, paramName) ? parameters[paramName] : '';
	};

	const getParameterValue = (paramName: string): unknown => {
		return Object.prototype.hasOwnProperty.call(serviceConnection.parameters, paramName) ? serviceConnection.parameters[paramName] : '';
	};

	const getParameterValueOptionsState = (environmentId: string | undefined, paramName: string): ParameterValueOptionsState | undefined => {
		return parameterValueOptions[getParameterValueOptionsKey(environmentId, paramName)];
	};

	// Helper function to clean parameters by removing system/metadata fields and converting empty values to null
	const cleanParameters = (
		parameters: { [key: string]: any },
		metadataParameters?: ParameterMetadata[],
		visibilityParameterValues: { [key: string]: any } = parameters,
	): { [key: string]: any } => {
		const cleanedParams: { [key: string]: any } = {};
		const systemFields = ['EnvironmentParametersServiceConnectionInstanceId', 'EnvironmentParametersId', '__jObject', 'Id', 'id'];
		const metadataByName = new Map(metadataParameters?.map((param) => [param.name, param]));

		Object.keys(parameters).forEach((key) => {
			if (!systemFields.includes(key)) {
				const param = metadataByName.get(key);
				if (!param || !isParameterVisible(param, visibilityParameterValues)) {
					return;
				}

				const value = parameters[key];
				// Emit null for empty/whitespace-only strings, otherwise keep the value
				cleanedParams[key] = typeof value === 'string' && value.trim() === '' ? null : value;
			}
		});

		return cleanedParams;
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			fullScreen={isMaximized}
			fullWidth
			maxWidth={false}
			PaperProps={{
				ref: paperRef,
				sx: isMaximized
					? { width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', resize: 'none', overflow: 'hidden' }
					: { width: storedSize.width, height: storedSize.height, maxWidth: '95vw', maxHeight: '90vh', resize: 'both', overflow: 'hidden' },
			}}
		>
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<Box display="flex" alignItems="center" gap={1} minWidth={0}>
					{(serviceConnectionMetadata?.iconUrl || serviceConnectionMetadata?.iconName) && (
						<IconComponent iconUrl={serviceConnectionMetadata.iconUrl} iconName={serviceConnectionMetadata.iconName} />
					)}
					{dialogTitle}
					<ServiceConnectionObsoleteIndicator categories={serviceConnectionMetadata?.categories} />
					<PresenceIndicator viewers={viewers} />
					{isReadOnly && <Chip label="System" color="info" variant="outlined" size="small" />}
				</Box>
				<Box sx={{ flex: 1 }} />
				<DialogHeaderActions isMaximized={isMaximized} onToggleMaximized={() => setIsMaximized((value) => !value)} onClose={handleClose} />
			</DialogTitle>
			<DialogContent dividers sx={{ flex: 1, overflowY: 'auto' }}>
				<ServiceConnectionObsoleteIndicator categories={serviceConnectionMetadata?.categories} variant="warning" />
				<Box>
					<Stack direction="column" spacing={2}>
						{/* Service Connection Type Field */}
						{isLoadingServiceConnectionMetadata ? (
							<Box display="flex" alignItems="center" gap={2}>
								<CircularProgress size={20} />
								<Typography variant="body2" color="textSecondary">
									Loading service connection types...
								</Typography>
							</Box>
						) : (
							<Stack spacing={1}>
								<Typography variant="caption" color="textSecondary">
									Type *
								</Typography>
								<Box
									sx={{
										p: 1.5,
										border: 1,
										borderColor: 'divider',
										borderRadius: 1,
										bgcolor: 'action.hover',
									}}
								>
										<Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="stretch">
											<Stack spacing={1} sx={{ width: { xs: '100%', md: 300 }, flexShrink: 0, minWidth: 0 }}>
											<TextField
												placeholder="Search types"
												size="small"
												value={serviceConnectionTypeSearch}
												onChange={(event) => setServiceConnectionTypeSearch(event.target.value)}
												fullWidth
												disabled={isEditingType}
												autoComplete="off"
												InputProps={{
													startAdornment: (
														<InputAdornment position="start">
															<SearchIcon fontSize="small" />
														</InputAdornment>
													),
													endAdornment: serviceConnectionTypeSearch ? (
														<InputAdornment position="end">
															<Tooltip title="Clear search">
																<IconButton
																	size="small"
																	edge="end"
																	onClick={() => setServiceConnectionTypeSearch('')}
																	disabled={isEditingType}
																	aria-label="Clear type search"
																>
																	<CloseIcon fontSize="small" />
																</IconButton>
															</Tooltip>
														</InputAdornment>
													) : undefined,
												}}
											/>
											<Stack
												direction={{ xs: 'row', md: 'column' }}
												spacing={0.75}
												sx={{
													maxHeight: { md: 224 },
													overflowX: { xs: 'auto', md: 'hidden' },
													overflowY: { xs: 'hidden', md: 'auto' },
													pb: { xs: 0.25, md: 0 },
													pr: { md: 0.5 },
												}}
											>
												{[null, ...serviceConnectionTypeCategories].map((category) => {
													const selected = serviceConnectionTypeCategory === category;
													const label = category ?? 'All Categories';
													const disabled = category !== null && !matchingServiceConnectionTypeCategories.has(category);
													return (
														<Box
															key={category ?? 'all'}
															component="button"
															type="button"
															disabled={disabled}
															onClick={() => setServiceConnectionTypeCategory(category)}
															aria-pressed={selected}
															sx={{
																border: 1,
																borderColor: selected ? 'primary.main' : 'divider',
																borderRadius: 1,
																bgcolor: selected ? 'action.selected' : 'rgba(255, 255, 255, 0.06)',
																color: disabled ? 'text.disabled' : 'text.primary',
																cursor: disabled ? 'not-allowed' : 'pointer',
																font: 'inherit',
																minHeight: 32,
																opacity: disabled ? 0.55 : 1,
																px: 1.25,
																py: 0.5,
																textAlign: 'left',
																width: { xs: 'auto', md: '100%' },
																flexShrink: 0,
																'&:hover': {
																	borderColor: disabled ? 'divider' : 'primary.main',
																	bgcolor: disabled ? (selected ? 'action.selected' : 'rgba(255, 255, 255, 0.06)') : selected ? 'action.selected' : 'rgba(255, 255, 255, 0.1)',
																},
															}}
														>
															<Typography variant="body2" noWrap>
																{label}
															</Typography>
														</Box>
													);
												})}
											</Stack>
										</Stack>
										<Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
											<Stack spacing={1.5} sx={{ maxHeight: { xs: 'none', md: 270 }, overflowY: { md: 'auto' }, pr: { md: 0.5 }, minWidth: 0, flex: '1 1 0' }}>
												{isMobile && (
													<Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
														Connectors
													</Typography>
												)}
										{filteredServiceConnectionTypeGroups.length === 0 ? (
											<Typography variant="body2" color="textSecondary" sx={{ p: 1 }}>
												No service connection types found
											</Typography>
										) : (
											filteredServiceConnectionTypeGroups.map(([category, options]) => (
												<Box key={category}>
													<Typography variant="overline" color="textSecondary" sx={{ display: 'block', lineHeight: 1.8 }}>
														{category}
													</Typography>
													<Box
														sx={{
															display: 'grid',
																	gridTemplateColumns: { xs: 'repeat(auto-fill, minmax(132px, 1fr))', md: 'repeat(auto-fill, minmax(156px, 1fr))' },
															gap: 1,
														}}
													>
														{options.map((option) => {
															const selected = selectedServiceConnectionType?.name === option.name;
															const displayName = option.displayName ?? option.name;
															return (
																<Box
																	key={option.name}
																	component="button"
																	type="button"
																	disabled={isEditingType}
																	onClick={() => {
																		setServiceConnection((prev) => {
																			const name = mode === 'create' ? `${displayName.replace(/\s+/g, '')}Connection` : prev.name;
																			return { ...prev, type: option.name, name };
																		});
																	}}
																	aria-pressed={selected}
																	sx={{
																		display: 'flex',
																		alignItems: 'center',
																		gap: 1,
																		minHeight: 56,
																		p: 1,
																		border: 1,
																		borderColor: selected ? 'primary.main' : 'divider',
																		borderRadius: 1,
																		bgcolor: selected ? 'primary.main' : 'rgba(255, 255, 255, 0.08)',
																		boxShadow: selected ? 3 : 1,
																		color: selected ? 'primary.contrastText' : 'text.primary',
																		cursor: isEditingType ? 'default' : 'pointer',
																		font: 'inherit',
																		textAlign: 'left',
																		transition: 'box-shadow 120ms ease, border-color 120ms ease, background-color 120ms ease, transform 120ms ease',
																		width: '100%',
																		'&:hover': {
																			borderColor: isEditingType ? (selected ? 'primary.main' : 'divider') : 'primary.main',
																			bgcolor: isEditingType ? (selected ? 'primary.main' : 'rgba(255, 255, 255, 0.08)') : selected ? 'primary.main' : 'rgba(255, 255, 255, 0.12)',
																			boxShadow: isEditingType ? (selected ? 3 : 1) : 4,
																			transform: isEditingType ? 'none' : 'translateY(-1px)',
																		},
																		'&:disabled': {
																			color: 'text.primary',
																			opacity: 1,
																		},
																	}}
																>
																	{option.iconUrl || option.iconName ? (
																		<IconComponent iconUrl={option.iconUrl} iconName={option.iconName} />
																	) : (
																		<Box
																			sx={{
																				width: 24,
																				height: 24,
																				borderRadius: 1,
																				bgcolor: 'action.selected',
																				display: 'flex',
																				alignItems: 'center',
																				justifyContent: 'center',
																				flexShrink: 0,
																			}}
																		>
																			<Typography variant="caption">{displayName.slice(0, 1).toUpperCase()}</Typography>
																		</Box>
																	)}
																	<Stack spacing={0.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
																		<Typography variant="body2" sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>
																			{displayName}
																		</Typography>
																		<ServiceConnectionObsoleteIndicator categories={option.categories} />
																	</Stack>
																</Box>
															);
														})}
													</Box>
												</Box>
											))
										)}
										</Stack>
									</Stack>
								</Box>
							</Stack>
						)}
						<Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
							<TextField
								label="Name"
								required
								value={serviceConnection.name}
								disabled={isReadOnly}
								onChange={(e) => setServiceConnection((prev) => ({ ...prev, name: e.target.value }))}
								fullWidth
								variant="outlined"
								sx={{ flex: '1 1 auto' }}
							/>
							{!isLoadingEnvironments && environments && environments.length > 0 && (
								<Box sx={{ flex: { xs: '1 1 auto', md: '0 0 auto' }, pt: { xs: 0, md: 0.25 } }}>
									<Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5 }}>
										Enabled Environments
									</Typography>
									<Stack direction="row" flexWrap="wrap" columnGap={2} rowGap={0.5} alignItems="center">
										<FormControlLabel
											control={
												<Switch
											checked={(serviceConnection.enabledEnvironments ?? []).length === 0}
											disabled={isReadOnly}
													onChange={(e) => {
														setServiceConnection((prev) => ({
															...prev,
															enabledEnvironments: e.target.checked ? [] : environments.map((env) => env.id),
														}));
													}}
												/>
											}
											label="All"
										/>
										{environments.map((env) => {
											const allEnabled = (serviceConnection.enabledEnvironments ?? []).length === 0;
											return (
												<FormControlLabel
													key={env.id}
													control={
														<Switch
															checked={allEnabled || (serviceConnection.enabledEnvironments ?? []).includes(env.id)}
													disabled={isReadOnly || allEnabled}
															onChange={(e) => {
																setServiceConnection((prev) => {
																	const current = prev.enabledEnvironments ?? [];
																	return {
																		...prev,
																		enabledEnvironments: e.target.checked ? [...current, env.id] : current.filter((id) => id !== env.id),
																	};
																});
															}}
														/>
													}
													label={env.name}
												/>
											);
										})}
									</Stack>
								</Box>
							)}
						</Stack>
						<Tabs value={dialogTab} onChange={(_, newValue) => setDialogTab(newValue)}>
							<Tab label="Configuration" />
							<Tab label="Guide" disabled={!hasGuideTab} />
						</Tabs>
						<Box hidden={dialogTab !== 0}>
							<Stack direction="column" spacing={2}>

					{/* Parameters Section with Tabs */}
					{isLoadingServiceConnectionMetadata ? (
						<Box display="flex" alignItems="center" gap={2}>
							<CircularProgress size={20} />
							<Typography variant="body2" color="textSecondary">
								Loading service connection configuration...
							</Typography>
						</Box>
					) : (
						serviceConnectionMetadata?.parameters && (
							<Box component={Paper} variant="outlined" sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: 2, minWidth: 0, containerType: 'inline-size', containerName: 'connection-parameters' }}>
								{useWizard && (
									<Stepper activeStep={activeWizardStep} alternativeLabel sx={{ mt: 2, mb: 3 }}>
										{wizardSteps.map((step) => (
											<Step key={step.key}>
												<StepLabel>{step.title}</StepLabel>
											</Step>
										))}
									</Stepper>
								)}
								<Typography variant="subtitle1" fontWeight={600} gutterBottom>
									{activeWizardStepMetadata?.title ?? 'Parameters'}
								</Typography>
								{activeWizardStepMetadata?.description && (
									<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
										{activeWizardStepMetadata.description}
									</Typography>
								)}
								<Divider sx={{ mb: 2 }} />
								{serviceConnection.isPerEnvironment && activeNonEnvironmentParameters.length > 0 && (
									<Box sx={{ mb: 2 }}>
										<Typography variant="subtitle2" gutterBottom>
											Connection Settings
										</Typography>
										<ParameterGrid>
											{activeNonEnvironmentParameters.map((param) => {
												if (!isParameterVisible(param, serviceConnection.parameters)) {
													return null;
												}

												const valueOptionsState = getParameterValueOptionsState(undefined, param.name);
												return (
													<ParameterTextField
														key={param.name}
														param={param}
														value={getParameterValue(param.name)}
														onChange={(value) => setServiceConnection((prev) => ({ ...prev, parameters: { ...prev.parameters, [param.name]: value } }))}
												valueOptions={valueOptionsState?.options}
												readOnly={isReadOnly}
														isLoadingValueOptions={valueOptionsState?.isLoading}
														hasValueOptionsLookup={valueOptionsState?.hasLookup}
													/>
												);
											})}
										</ParameterGrid>
									</Box>
								)}
								{activeEnvironmentParameters.length > 0 && (
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 2,
											flexWrap: 'wrap',
											borderBottom: 1,
											borderColor: 'divider',
											mb: 2,
											minHeight: 48,
										}}
									>
										<Tabs
											value={serviceConnection.isPerEnvironment ? activeTab : 0}
											onChange={(_, newValue) => {
												if (serviceConnection.isPerEnvironment) {
													setActiveTab(newValue);
												}
											}}
											variant="scrollable"
											scrollButtons="auto"
											sx={{ flex: '1 1 240px', minWidth: 0, '& .MuiTabs-indicator': { height: 2 } }}
										>
											{serviceConnection.isPerEnvironment ? (
												environments && environments.length > 0 ? (
													environments.map((env) => <Tab key={env.id} label={env.name} />)
												) : (
													<Tab label={isLoadingEnvironments ? 'Loading environments...' : 'No environments'} disabled />
												)
											) : (
												<Tab label="All Environments" />
											)}
										</Tabs>
										<FormControlLabel
											sx={{ mr: 0, ml: 1, flexShrink: 0, '& .MuiFormControlLabel-label': { whiteSpace: 'nowrap' } }}
											labelPlacement="start"
											control={
												<Switch
											checked={serviceConnection.isPerEnvironment}
											disabled={isReadOnly}
													onChange={(e) => {
														const usePerEnvironment = e.target.checked;
														const metadataParameters = serviceConnectionMetadata?.parameters ?? [];
														const nonEnvironmentParameters = getNonEnvironmentScopedParameters(metadataParameters);
														const environmentParameters = getEnvironmentScopedParameters(metadataParameters);
														setServiceConnection((prev) => {
															if (usePerEnvironment) {
																return {
																	...prev,
																	isPerEnvironment: true,
																	parameters: pickParameterValues(prev.parameters, nonEnvironmentParameters),
																	environmentParameters: (environments ?? []).map((env) => {
																		const existingEnvParameters = prev.environmentParameters.find((ep) => ep.environmentId === env.id)?.parameters ?? {};
																		const parameters = { ...pickParameterValues(prev.parameters, environmentParameters), ...existingEnvParameters };
																		environmentParameters.forEach((param) => {
																			if (parameters[param.name] === undefined) {
																				parameters[param.name] = param.defaultValue ?? '';
																			}
																		});
																		return { environmentId: env.id, parameters };
																	}),
																};
															}

															const firstEnvironmentParameters = prev.environmentParameters[activeTab]?.parameters ?? prev.environmentParameters[0]?.parameters ?? {};
															return {
																...prev,
																isPerEnvironment: false,
																parameters: {
																	...prev.parameters,
																	...pickParameterValues(firstEnvironmentParameters, environmentParameters),
																},
																environmentParameters: [],
															};
														});
													}}
												/>
											}
											label="Per Environment"
										/>
									</Box>
								)}
								<Stack spacing={2}>
									{serviceConnection.isPerEnvironment ? (
										<>
											{activeEnvironmentParameters.length === 0 ? null : isLoadingEnvironments ? (
												<Box display="flex" justifyContent="center" alignItems="center" py={3}>
													<CircularProgress size={24} />
													<Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
														Loading environments...
													</Typography>
												</Box>
											) : environments && environments.length > 0 ? (
												<>
													{environments.map((env, index) => (
														<Box key={env.id} hidden={activeTab !== index}>
															<ParameterGrid>
																{activeEnvironmentParameters.map((param) => {
																	const envParams = serviceConnection.environmentParameters.find((ep) => ep.environmentId === env.id)?.parameters ?? {};
																	const visibilityValues = { ...serviceConnection.parameters, ...envParams };
																	if (!isParameterVisible(param, visibilityValues)) {
																		return null;
																	}

																	const valueOptionsState = getParameterValueOptionsState(env.id, param.name);
																	return (
																		<ParameterTextField
																			key={`${env.id}-${param.name}`}
																			param={param}
																			value={getEnvironmentParameterValue(env.id, param.name)}
																			onChange={(value) => handleEnvironmentParameterChange(env.id, param.name, value)}
																			keyPrefix={env.id}
																	valueOptions={valueOptionsState?.options}
																	readOnly={isReadOnly}
																			isLoadingValueOptions={valueOptionsState?.isLoading}
																			hasValueOptionsLookup={valueOptionsState?.hasLookup}
																		/>
																	);
																})}
															</ParameterGrid>
														</Box>
													))}
												</>
											) : (
												<Typography variant="body2" color="textSecondary">
													No environments found.
												</Typography>
											)}
										</>
									) : (
										<>
											<Box>
												<ParameterGrid>
													{[...activeNonEnvironmentParameters, ...activeEnvironmentParameters].map((param) => {
														if (!isParameterVisible(param, serviceConnection.parameters)) {
															return null;
														}

														const valueOptionsState = getParameterValueOptionsState(undefined, param.name);
														return (
															<ParameterTextField
																key={param.name}
																param={param}
																value={getParameterValue(param.name)}
																onChange={(value) => setServiceConnection((prev) => ({ ...prev, parameters: { ...prev.parameters, [param.name]: value } }))}
												valueOptions={valueOptionsState?.options}
												readOnly={isReadOnly}
																isLoadingValueOptions={valueOptionsState?.isLoading}
																hasValueOptionsLookup={valueOptionsState?.hasLookup}
															/>
														);
													})}
												</ParameterGrid>
											</Box>
										</>
									)}
								</Stack>
							</Box>
						)
					)}

					{mode === 'edit' &&
						(isLoadingServiceConnectionProperties || Object.values(serviceConnectionProperties).some((properties) => properties.length > 0)) && (
							<Box>
								<Typography variant="subtitle2" gutterBottom mt={2}>
									Properties
								</Typography>
								<Divider sx={{ mb: 2 }} />
								{isLoadingEnvironments ? (
									<Box display="flex" justifyContent="center" alignItems="center" py={3}>
										<CircularProgress size={24} />
										<Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
											Loading environments...
										</Typography>
									</Box>
								) : environments && environments.length > 0 ? (
									<>
										<Tabs value={propertiesTab} onChange={(_, newValue) => setPropertiesTab(newValue)} variant="scrollable" scrollButtons="auto">
											{environments.map((env) => (
												<Tab key={env.id} label={env.name} />
											))}
										</Tabs>
										{environments.map((env, index) => (
											<Box key={env.id} hidden={propertiesTab !== index} sx={{ pt: 2 }}>
												<PropertiesContent isLoading={isLoadingServiceConnectionProperties} properties={serviceConnectionProperties[env.id]} />
											</Box>
										))}
									</>
								) : (
									<Box sx={{ pt: 2 }}>
										<PropertiesContent isLoading={isLoadingServiceConnectionProperties} properties={serviceConnectionProperties[defaultEnvironmentKey]} />
									</Box>
								)}
							</Box>
						)}
							</Stack>
						</Box>
					</Stack>
				</Box>
				<Box
					hidden={dialogTab !== 1}
					sx={{
						mt: 2,
						p: { xs: 1.5, sm: 3 },
						bgcolor: 'action.hover',
						borderRadius: 2,
					}}
				>
					<Stack direction="row" justifyContent="flex-end" gap={0.5} sx={{ maxWidth: 1120, mx: 'auto', mb: 1 }}>
						<Tooltip title="Open manual in a new tab">
							<IconButton
								aria-label="Open manual in a new tab"
								disabled={!serviceConnection.type}
								onClick={() => window.open(`/service-connections/manual?type=${encodeURIComponent(serviceConnection.type)}`, '_blank', 'noopener,noreferrer')}
							>
								<OpenInNewIcon />
							</IconButton>
						</Tooltip>
						<CopyToClipboardButton
							value={serviceConnectionManual ?? ''}
							title="Copy manual to clipboard"
							edge={false}
							disabled={!serviceConnectionManual}
						/>
					</Stack>
					<Paper
						variant="outlined"
						sx={{
							maxWidth: 1120,
							minHeight: 240,
							mx: 'auto',
							p: { xs: 2, sm: 4 },
							borderColor: 'divider',
							borderRadius: 2,
							boxShadow: 1,
						}}
					>
						{isLoadingServiceConnectionManual ? (
							<Box display="flex" alignItems="center" justifyContent="center" gap={2} minHeight={176}>
								<CircularProgress size={20} />
								<Typography variant="body2" color="textSecondary">
									Loading guide...
								</Typography>
							</Box>
						) : (
							<StyledMarkdown>{serviceConnectionManual ?? ''}</StyledMarkdown>
						)}
					</Paper>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>{isReadOnly ? 'Close' : 'Cancel'}</Button>
				{useWizard && activeWizardStep > 0 && <Button onClick={handlePreviousWizardStep}>Back</Button>}
				{useWizard && !isLastWizardStep ? (
					<Button onClick={handleNextWizardStep} variant="contained" color="primary" disabled={saving || !isCurrentWizardStepValid}>
						Next
					</Button>
				) : !isReadOnly ? (
					<Button onClick={handleSave} variant="contained" color="primary" disabled={saving || !isFormValid} loading={saving}>
						{mode === 'create' ? 'Create' : 'Update'}
					</Button>
				) : null}
			</DialogActions>
		</Dialog>
	);
}
